import { useMutation } from '@tanstack/react-query';
import * as AlertDialog from '@givebutter/primitives/alert-dialog';
import { Loader } from '@givebutter/primitives/loader';
import { useToast } from '@givebutter/primitives/toast';
import { deleteActivity } from 'api/contacts/activities';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentContact } from 'customHooks/useCurrentContact';
import { accountQueryKeys } from 'queries/account-query-keys';
import { queryClient } from 'queries/queryClient';
import { GivebutterAxiosError } from 'types/error';

const DeleteItem = ({ itemId, children }: { itemId: number; children: React.ReactNode }) => {
    const { accountId } = useCurrentAccount();
    const { data: contact } = useCurrentContact();
    const contactId = contact.id;
    const { success, error } = useToast();

    const { mutate: deleteNote, isPending: isLoading } = useMutation({
        mutationFn: () => {
            return deleteActivity(accountId, contactId, {
                activityId: itemId,
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.contactById(accountId, contactId),
            });
            success(
                <>
                    <span className="font-medium">Success!</span> Deleted successfully.
                </>,
            );
        },
        onError: (e: GivebutterAxiosError) => {
            error(e.response?.data.message || 'Unable to delete activity.');
        },
    });

    return (
        <AlertDialog.Root type="error">
            {children}
            <AlertDialog.Portal>
                <AlertDialog.Overlay />
                <AlertDialog.Content>
                    {isLoading && <Loader />}
                    <AlertDialog.Title>Are you sure?</AlertDialog.Title>
                    <AlertDialog.Description>
                        Are you sure you want to delete this? You will not be able to undo this
                        action.
                    </AlertDialog.Description>
                    <AlertDialog.Footer>
                        <AlertDialog.Cancel>Cancel</AlertDialog.Cancel>
                        <AlertDialog.Action
                            disabled={isLoading}
                            onClick={e => {
                                e.preventDefault();
                                deleteNote();
                            }}
                        >
                            Confirm
                        </AlertDialog.Action>
                    </AlertDialog.Footer>
                </AlertDialog.Content>
            </AlertDialog.Portal>
        </AlertDialog.Root>
    );
};

const Root = DeleteItem;
const Trigger = AlertDialog.Trigger;
const DeleteItemTrigger = Trigger;

export {
    Root,
    Trigger,
    //
    DeleteItem,
    DeleteItemTrigger,
};
